import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import type { PropertyCategory } from '../common/property-taxonomy.schema';

// The catalog's AUF.BESICHTIGUNG.ART minus „keine Besichtigung“.
export const PROPERTY_CAPTURE_VIEWING_KIND_VALUES = [
  'INTERIOR_AND_EXTERIOR',
  'EXTERIOR_ONLY',
  'PARTIAL',
  'VIDEO',
] as const;
export const PropertyCaptureViewingKindSchema = z.enum(PROPERTY_CAPTURE_VIEWING_KIND_VALUES);
export type PropertyCaptureViewingKind = z.infer<typeof PropertyCaptureViewingKindSchema>;

export const PROPERTY_CAPTURE_STATUS_VALUES = ['IN_PROGRESS', 'COMPLETED'] as const;
export const PropertyCaptureStatusSchema = z.enum(PROPERTY_CAPTURE_STATUS_VALUES);
export type PropertyCaptureStatus = z.infer<typeof PropertyCaptureStatusSchema>;

export const CAPTURABLE_PROPERTY_CATEGORIES = [
  'HOUSE',
  'APARTMENT',
  'LAND',
] as const satisfies ReadonlyArray<PropertyCategory>;

export const PropertyCaptureSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  status: PropertyCaptureStatusSchema,
  viewingKind: PropertyCaptureViewingKindSchema,
  startedAt: z.iso.datetime(),
  startedByMemberId: refersTo('member', z.uuid()),
  completedAt: z.iso.datetime().nullable(),
  completedByMemberId: refersTo('member', z.uuid()).nullable(),
  openRequiredFieldCount: z.number().int().nonnegative(),
  openRequiredPhotoCount: z.number().int().nonnegative(),
});
export type PropertyCaptureResponse = z.infer<typeof PropertyCaptureSchema>;

export const GetPropertyCaptureResponseSchema = z.object({ capture: PropertyCaptureSchema.nullable() });
export type GetPropertyCaptureResponse = z.infer<typeof GetPropertyCaptureResponseSchema>;

export const StartPropertyCaptureBodySchema = z.object({ viewingKind: PropertyCaptureViewingKindSchema }).strict();
export type StartPropertyCaptureBody = z.infer<typeof StartPropertyCaptureBodySchema>;

export const ChangePropertyCaptureViewingKindBodySchema = StartPropertyCaptureBodySchema;
export type ChangePropertyCaptureViewingKindBody = StartPropertyCaptureBody;

export const CAPTURE_FINDING_ANSWER_VALUES = ['VALUE', 'NOT_DETERMINABLE', 'NOT_APPLICABLE', 'CLEARED'] as const;
export type CaptureFindingAnswer = (typeof CAPTURE_FINDING_ANSWER_VALUES)[number];

export const CAPTURE_FIELD_NEED_VALUES = ['REQUIRED', 'RECOMMENDED'] as const;
export const CAPTURE_INPUT_VALUE_TYPES = ['text', 'number', 'enum', 'boolean', 'date'] as const;
export const CAPTURE_ANSWER_KIND_VALUES = ['VALUE', 'NOT_DETERMINABLE', 'NOT_APPLICABLE'] as const;

const CaptureInputShape = {
  valueType: z.enum(CAPTURE_INPUT_VALUE_TYPES),
  unit: z.string().nullable(),
  valueList: z.array(z.string()),
};

export const CaptureFieldInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SCALAR'), ...CaptureInputShape }),
  z.object({
    kind: z.literal('COMPOSITE'),
    subfields: z.array(z.object({ name: z.string(), label: z.string(), ...CaptureInputShape })).min(1),
  }),
  z.object({ kind: z.literal('NONE') }),
]);
export type CaptureFieldInput = z.infer<typeof CaptureFieldInputSchema>;

export const CaptureOccurrenceSchema = z.object({
  occurrence: z.string().nullable(),
  captured: z
    .object({
      answer: z.enum(CAPTURE_ANSWER_KIND_VALUES),
      value: z.unknown().nullable(),
      recordedAt: z.iso.datetime(),
    })
    .nullable(),
  known: z.unknown().nullable(),
});
export type CaptureOccurrence = z.infer<typeof CaptureOccurrenceSchema>;

export const CaptureFieldSchema = z.object({
  factKey: z.string(),
  label: z.string(),
  need: z.enum(CAPTURE_FIELD_NEED_VALUES),
  open: z.boolean(),
  occurrenceNoun: z.string().nullable(),
  input: CaptureFieldInputSchema,
  occurrences: z.array(CaptureOccurrenceSchema).min(1),
});
export type CaptureField = z.infer<typeof CaptureFieldSchema>;

export const CaptureFieldGroupSchema = z.object({
  groupId: z.string(),
  missing: z.array(CaptureFieldSchema),
  known: z.array(CaptureFieldSchema),
});
export type CaptureFieldGroup = z.infer<typeof CaptureFieldGroupSchema>;

export const PropertyCaptureFieldsResponseSchema = z.object({
  captureId: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  openCount: z.number().int().nonnegative(),
  groups: z.array(CaptureFieldGroupSchema),
});
export type PropertyCaptureFieldsResponse = z.infer<typeof PropertyCaptureFieldsResponseSchema>;

export const RecordCaptureFindingBodySchema = z
  .object({
    factKey: z.string().min(1).max(120),
    occurrence: z.string().trim().min(1).max(80).nullable(),
    answer: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('VALUE'), value: z.unknown() }),
      z.object({ kind: z.literal('NOT_DETERMINABLE') }),
      z.object({ kind: z.literal('NOT_APPLICABLE') }),
      z.object({ kind: z.literal('CLEARED') }),
    ]),
  })
  .strict();
export type RecordCaptureFindingBody = z.infer<typeof RecordCaptureFindingBodySchema>;

// The media label every capture photo is uploaded under; the discriminator both guards read.
export const CAPTURE_PHOTO_MEDIA_DESCRIPTION = 'property-capture-photo';
export const CAPTURE_PHOTO_CAP = 250;

export const PHOTO_MOTIF_REQUIREMENT_VALUES = ['REQUIRED', 'CONDITIONAL'] as const;

export const CapturePhotoMotifSchema = z.object({
  motifId: z.string(),
  name: z.string(),
  requirement: z.enum(PHOTO_MOTIF_REQUIREMENT_VALUES),
  condition: z.string().nullable(),
  instruction: z.string(),
  minimumCount: z.number().int().positive(),
  takenCount: z.number().int().nonnegative(),
});
export type CapturePhotoMotif = z.infer<typeof CapturePhotoMotifSchema>;

export const CapturePhotoSchema = z.object({
  id: z.uuid(),
  motifId: z.string(),
  motifName: z.string(),
  takenAt: z.iso.datetime(),
  takenByMemberId: refersTo('member', z.uuid()),
  takenByName: z.string().nullable(), // null when no longer an active member
  url: z.string(), // short-lived inline URL of the original
  urlExpiresInSeconds: z.number().int().positive(),
});
export type CapturePhoto = z.infer<typeof CapturePhotoSchema>;

export const PropertyCapturePhotosResponseSchema = z.object({
  captureId: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  photoCount: z.number().int().nonnegative(),
  openRequiredCount: z.number().int().nonnegative(),
  motifs: z.array(CapturePhotoMotifSchema), // REQUIRED first, then CONDITIONAL, protocol order
  photos: z.array(CapturePhotoSchema), // oldest first
});
export type PropertyCapturePhotosResponse = z.infer<typeof PropertyCapturePhotosResponseSchema>;

export const ValuationAgentPropertyCaptureResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  capture: z
    .object({
      id: z.uuid(),
      status: PropertyCaptureStatusSchema,
      viewingKind: PropertyCaptureViewingKindSchema,
      startedAt: z.iso.datetime(),
      completedAt: z.iso.datetime().nullable(),
    })
    .nullable(),
  photos: z.array(
    z.object({
      id: z.uuid(),
      motifId: z.string(),
      motifName: z.string(),
      takenAt: z.iso.datetime(),
      contentType: z.string(),
      url: z.string(),
      urlExpiresInSeconds: z.number().int().positive(),
    }),
  ),
});
export type ValuationAgentPropertyCaptureResponse = z.infer<typeof ValuationAgentPropertyCaptureResponseSchema>;

export const CreateCapturePhotoUploadBodySchema = z
  .object({
    motifId: z.string().min(1).max(120),
    contentType: z.string(),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().max(255).nullable(),
  })
  .strict();
export type CreateCapturePhotoUploadBody = z.infer<typeof CreateCapturePhotoUploadBodySchema>;

export const ConfirmCapturePhotoUploadBodySchema = z
  .object({ motifId: z.string().min(1).max(120), fileId: z.uuid() })
  .strict();
export type ConfirmCapturePhotoUploadBody = z.infer<typeof ConfirmCapturePhotoUploadBodySchema>;

/** The next generated occurrence label for a noun: one past the highest `{noun} {n}` among `existing`. Shared by app and server. */
export function nextOccurrenceLabel(noun: string, existing: ReadonlyArray<string | null>): string {
  const pattern = new RegExp(`^${noun.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ([1-9][0-9]*)$`);
  const highest = existing.reduce((max, label) => {
    const match = label === null ? null : pattern.exec(label);

    return match === null ? max : Math.max(max, Number(match[1]));
  }, 0);

  return `${noun} ${highest + 1}`;
}
